import type { GridOptions, PaginationChangedEvent } from "ag-grid-community";
import React from "react";

/** Shared default for SSRM admin tables — keep a stable array reference for memoization. */
export const ADMIN_BILLING_PAGINATION_PAGE_SIZE_SELECTOR: readonly number[] = [
  25, 50, 100, 250, 500,
];

export type UseServerSidePaginationGridPropsParams = {
  paginationPageSize: number;
  paginationPageSizeSelector?: readonly number[] | false;
};

type ServerSidePaginationGridProps<TData> = Pick<
  GridOptions<TData>,
  | "cacheBlockSize"
  | "onPaginationChanged"
  | "pagination"
  | "paginationPageSize"
  | "paginationPageSizeSelector"
>;

/**
 * SSRM loads rows in blocks (`cacheBlockSize`, default 100). With pagination, block size must stay
 * ≥ page size when the user changes page size via the selector — otherwise AG Grid’s SSRM + pagination
 * combo is unsupported. This hook keeps both in sync and purges the server-side cache on resize.
 */
export function useServerSidePaginationGridProps<TData>(
  params: UseServerSidePaginationGridPropsParams,
): ServerSidePaginationGridProps<TData> {
  const {
    paginationPageSize,
    paginationPageSizeSelector = ADMIN_BILLING_PAGINATION_PAGE_SIZE_SELECTOR,
  } = params;

  const onPaginationChanged = React.useCallback(
    (event: PaginationChangedEvent<TData>) => {
      if (!event.newPageSize) return;
      const { api } = event;
      const pageSize = api.paginationGetPageSize();
      api.setGridOption("paginationPageSize", pageSize);
      api.setGridOption("cacheBlockSize", pageSize);
      api.refreshServerSide({ purge: true });
    },
    [],
  );

  return React.useMemo(
    () => ({
      pagination: true as const,
      paginationPageSize,
      paginationPageSizeSelector:
        paginationPageSizeSelector === false
          ? false
          : [...paginationPageSizeSelector],
      cacheBlockSize: paginationPageSize,
      onPaginationChanged,
    }),
    [paginationPageSize, paginationPageSizeSelector, onPaginationChanged],
  );
}
